import { useState, useEffect, useCallback } from 'react';
import type { ConfirmEmailChangeResult } from 'minka-shared';
import type {
  User,
  LoginCredentials,
  RegisterWithInvitationData,
  UpdateProfileData,
  UpdateProfileResult,
  AuthResponse,
  AuthContextType
} from '@/types/auth.types';
import authAPI from '@/services/auth-api';
import { registerWithInvitation as registerWithInvitationApi } from '@/services/invitation-api';

export function useAuth(): AuthContextType {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // A stored token is being checked: neither signed in nor signed out yet.
  const [isRestoringSession, setIsRestoringSession] = useState<boolean>(() => authAPI.isAuthenticated());
  
  
  // Simple initialization - just check if we have a valid token
  useEffect(() => {
    const hasToken = authAPI.isAuthenticated();
    if (hasToken) {
      // Try to get user profile, but don't block on it
      authAPI.getProfile()
        .then(profileData => {
          if (profileData.success && profileData.data) {
            setUser(profileData.data);
            setIsAuthenticated(true);
            // React now manages all user data - no GameManager needed
          }
        })
        .catch(() => {
          // If profile fails, clear token and stay logged out
          authAPI.logout();
        })
        .finally(() => setIsRestoringSession(false));
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await authAPI.login(credentials);
      
      if (result.success && result.data) {
        setUser(result.data.user);
        setIsAuthenticated(true);
        // React now manages all user data - no GameManager needed
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * The only signup path: an invitation token stands in for the email, which
   * the server reads from the invitation itself.
   */
  const registerWithInvitation = useCallback(
    async (data: RegisterWithInvitationData): Promise<AuthResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await registerWithInvitationApi(data);

        if (result?.user) {
          setUser(result.user);
          setIsAuthenticated(true);
        }

        return { success: true, data: result };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Registration failed';
        setError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /** Redeem a reset link; success signs the user in with the new password. */
  const resetPassword = useCallback(async (token: string, password: string): Promise<AuthResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authAPI.resetPassword(token, password);

      if (result.success && result.data) {
        setUser(result.data.user);
        setIsAuthenticated(true);
      }

      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authAPI.logout();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const updateProfile = useCallback(async (data: UpdateProfileData): Promise<UpdateProfileResult> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await authAPI.updateProfile(data);
      
      if (result.success && result.data) {
        // `data` still has the old email while a change waits for its link.
        setUser(result.data);
        return {
          user: result.data,
          ...(typeof result.pendingEmail === 'string' ? { pendingEmail: result.pendingEmail } : {})
        };
      } else {
        throw new Error(result.error || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Follow an email change link, signed in as the account that asked for the
   * change. The profile is reloaded afterwards so the new address shows.
   */
  const confirmEmailChange = useCallback(async (token: string): Promise<ConfirmEmailChangeResult> => {
    const result = await authAPI.confirmEmailChange(token);

    if (authAPI.isAuthenticated()) {
      try {
        const profile = await authAPI.getProfile();
        if (profile.success && profile.data) {
          setUser(profile.data);
        }
      } catch {
        // The confirmation stands; the next load of the profile shows it.
      }
    }

    return result;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    isAuthenticated,
    isRestoringSession,
    isLoading,
    error,
    login,
    registerWithInvitation,
    resetPassword,
    updateProfile,
    confirmEmailChange,
    logout,
    clearError
  };
};